import { AssessmentSettings } from "@/generated/graphql";
import { isExam, isExamInProgress } from "@/ui/task";
import { IReqsDifference } from "@/ui/task/requirements";

const settingsToCheck = [
  "wordLimit",
  "referencingStyle",
  "weight",
  "examReadingTime",
  "examWritingTime",
  "examEndDate",

  // these ones should not be able to be changed once students have started
  // working on the exam but just in case
  "assessmentType",
  "examTiming",
  "examStartDate",
  "examInactivityTimeout",
  "examIpFilter",
] satisfies (keyof IReqsDifference)[];

type SettingsToCheck = (typeof settingsToCheck)[number];

/**
 * @returns whether a modal should warn teachers about making changes
 * to exam settings while the assessment is happening.
 */
export const shouldShowWarning = (
  releasedRequirements: Pick<
    AssessmentSettings,
    SettingsToCheck | "examAutoSubmissionDisabled"
  > | null,
  diff?: Pick<
    IReqsDifference,
    | SettingsToCheck
    | "enableExamAutoSubmission"
    | "enableExamReentryPrevention"
    | "enableExamIpFilter"
  >
): boolean => {
  if (!releasedRequirements || !diff) return false;
  if (!isExam(releasedRequirements)) return false;
  if (!isExamInProgress(releasedRequirements)) return false;

  const relevantSettingsChanged = settingsToCheck.some(
    (setting) => diff[setting]
  );

  const autoSubmissionChanged =
    releasedRequirements.examAutoSubmissionDisabled ==
    diff.enableExamAutoSubmission;

  // These toggles are named differently on Requirements and on
  // AssessmentSettings, so they cannot go in `settingsToCheck` — that list is
  // keyed by names both types share. Switching the IP restriction on mid-exam
  // locks out every student not on the allowed network, whether or not the
  // range itself was edited, so the toggle needs checking on its own.
  return (
    relevantSettingsChanged ||
    autoSubmissionChanged ||
    diff.enableExamReentryPrevention ||
    diff.enableExamIpFilter
  );
};
